import React, { useState, useEffect, useRef } from "react";
import { X, Play, Rewind, Repeat, Mic, Send, Trash2, Share2, MessageCircle, PenLine, Loader2, Square, Users, Plus, Phone, Check } from "lucide-react";
import { getMessages, askText, askVoice, getReplyDraft, deleteDocument, saveDocument, API, tk, requestTTS, audioUrlForKey, listHelpers, addHelper, deleteHelper } from "../lib/api";
import { useAudio } from "./AudioProvider";
import { makeT } from "../lib/i18n";
import { toast } from "sonner";

function VerdictBadge({ verdict, importantLabel, junkLabel }) {
  const important = verdict === "important";
  return (
    <span
      data-testid="document-verdict-badge"
      className={`inline-flex items-center px-4 py-1.5 rounded-full text-base font-extrabold border-2 border-[#1A1C19] ${
        important ? "bg-[#15803D] text-white" : "bg-[#DC2626] text-white"
      }`}
    >
      {important ? importantLabel : junkLabel}
    </span>
  );
}

export default function DocumentModal({ doc, language, uiLang = "gu", onClose, onSaved, onDeleted }) {
  const { speak, stop, repeat, loadingKey } = useAudio();
  const t = makeT(uiLang);
  const [isSaved, setIsSaved] = useState(doc.saved !== false);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [reply, setReply] = useState(doc.reply_draft || null);
  const [replyLoading, setReplyLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [tab, setTab] = useState("read");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [helpers, setHelpers] = useState([]);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [sharing, setSharing] = useState(false);
  const [forwarding, setForwarding] = useState(false);

  useEffect(() => {
    getMessages(doc.id).then(setMessages).catch(() => {});
    listHelpers(doc.user_id).then(setHelpers).catch(() => {});
    return () => stop();
  }, [doc.id]); // eslint-disable-line

  const doAskText = async () => {
    if (!question.trim()) return;
    setAsking(true);
    const q = question;
    setQuestion("");
    try {
      const res = await askText(doc.id, q, language);
      const upd = await getMessages(doc.id);
      setMessages(upd);
      speak(res.answer, 1.0, "answer");
    } catch (e) {
      toast.error(t("ansErr"));
    } finally {
      setAsking(false);
    }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAsking(true);
        try {
          const res = await askVoice(doc.id, language, blob);
          const upd = await getMessages(doc.id);
          setMessages(upd);
          speak(res.answer, 1.0, "answer");
        } catch (e) {
          toast.error(t("voiceErr"));
        } finally {
          setAsking(false);
        }
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
    } catch (e) {
      toast.error(t("micErr"));
    }
  };
  const stopRec = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      setRecording(false);
    }
  };

  const loadReply = async () => {
    setReplyLoading(true);
    try {
      const r = await getReplyDraft(doc.id);
      setReply(r);
    } catch (e) {
      toast.error(t("replyErr"));
    } finally {
      setReplyLoading(false);
    }
  };

  const share = async () => {
    const text = `${doc.title}\n\n${doc.summary}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: doc.title, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success(t("copied"));
      }
    } catch (e) { /* cancelled */ }
  };

  const remove = async () => {
    await deleteDocument(doc.id);
    toast.success(t("deleted"));
    onDeleted();
  };

  const doSave = async () => {
    try {
      await saveDocument(doc.id);
      setIsSaved(true);
      toast.success(t("savedToast"));
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(t("sendErr"));
    }
  };

  const buildShareText = async () => {
    let audioUrl = "";
    try {
      const { key } = await requestTTS(doc.spoken_text, 1.0);
      audioUrl = audioUrlForKey(key);
    } catch (e) { /* audio optional */ }
    const body = doc.translation || doc.summary || "";
    let msg = `${doc.title}\n\n${body}`;
    if (audioUrl) msg += `\n\n🔊 ${audioUrl}`;
    if (imgUrl) msg += `\n\n🖼️ ${imgUrl}`;
    return msg;
  };

  // Fetch the document picture + generated voice clip as real File objects
  const getMediaFiles = async () => {
    const files = [];
    if (imgUrl) {
      try {
        const r = await fetch(imgUrl);
        const b = await r.blob();
        const ext = (firstFile.ext || "jpg").toLowerCase();
        files.push(new File([b], `${(doc.title || "document").slice(0, 40)}.${ext}`, { type: b.type || "image/jpeg" }));
      } catch (e) { /* skip picture */ }
    }
    try {
      const { key } = await requestTTS(doc.spoken_text, 1.0);
      const r = await fetch(audioUrlForKey(key));
      const b = await r.blob();
      files.push(new File([b], `${(doc.title || "voice").slice(0, 40)}.mp3`, { type: "audio/mpeg" }));
    } catch (e) { /* skip audio */ }
    return files;
  };

  // One tap: share ONLY the picture + voice clip (no text) via the phone's
  // native share sheet, which attaches the real files to WhatsApp/etc.
  const forwardAll = async () => {
    setForwarding(true);
    try {
      const files = await getMediaFiles();
      if (files.length && navigator.canShare && navigator.canShare({ files })) {
        await navigator.share({ files });
      } else if (files.length && navigator.share) {
        await navigator.share({ files });
      } else {
        toast.error(t("sendErr"));
      }
    } catch (e) { /* user cancelled */ }
    finally { setForwarding(false); }
  };


  const shareToHelper = async (helper) => {
    setSharing(true);
    try {
      const msg = await buildShareText();
      const url = `https://wa.me/${helper.phone}?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank");
    } catch (e) {
      toast.error(t("sendErr"));
    } finally {
      setSharing(false);
    }
  };

  const saveHelper = async () => {
    if (!newName.trim() || !newPhone.trim()) { toast.error(t("helperNeed")); return; }
    try {
      const h = await addHelper(doc.user_id, newName, newPhone);
      setHelpers((prev) => [...prev, h]);
      setNewName(""); setNewPhone("");
      toast.success(t("helperAdded"));
    } catch (e) {
      toast.error(t("helperErr"));
    }
  };

  const removeHelper = async (id) => {
    await deleteHelper(id);
    setHelpers((prev) => prev.filter((h) => h.id !== id));
  };

  const firstFile = doc.files && doc.files[0];
  const imgUrl = firstFile ? `${API}/files/${firstFile.storage_path}?tk=${tk()}` : null;
  const isImg = firstFile && firstFile.ext !== "pdf";

  const tabs = [
    { id: "read", label: t("tabRead"), icon: Play },
    { id: "ask", label: t("tabAsk"), icon: MessageCircle },
    { id: "reply", label: t("tabReply"), icon: PenLine },
    { id: "share", label: t("tabShare"), icon: Users },
  ];

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="document-modal" dir={uiLang === "ar" ? "rtl" : "ltr"}>
      <div className="bg-[#FAFAF7] w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border-3 border-[#1A1C19] yb-fade-up">
        <div className="sticky top-0 bg-[#FFD600] border-b-3 border-[#1A1C19] p-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h2 className="yb-heading text-xl font-extrabold truncate">{doc.title}</h2>
            <p className="text-sm text-[#1A1C19]/70 truncate">{doc.sender}</p>
          </div>
          <button onClick={onClose} data-testid="document-close" className="yb-tactile bg-white p-3 ml-2 shrink-0" aria-label="બંધ">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-3 flex-wrap">
            <VerdictBadge verdict={doc.verdict} importantLabel={t("important")} junkLabel={t("junk")} />
            {doc.has_deadline && (
              <span className={`px-3 py-1.5 rounded-full text-sm font-bold border-2 border-[#1A1C19] ${doc.urgency === "high" ? "bg-[#DC2626] text-white" : doc.urgency === "medium" ? "bg-[#D97706] text-white" : "bg-[#FEF3C7]"}`}>
                {doc.deadline_label || "તારીખ છે"}
              </span>
            )}
          </div>

          {!isSaved && (
            <div className="bg-[#FFF7CC] rounded-2xl border-3 border-[#FFD600] p-4 space-y-3" data-testid="save-bar" style={{ boxShadow: "0 4px 0 0 #E6C200" }}>
              <p className="font-bold text-[#1A1C19]">{t("saveHint")}</p>
              <div className="flex gap-2">
                <button onClick={doSave} data-testid="document-save" className="yb-tactile flex-1 py-3 bg-[#15803D] text-white font-extrabold flex items-center justify-center gap-2">
                  <Check className="w-6 h-6" strokeWidth={3} /> {t("save")}
                </button>
                <button onClick={remove} data-testid="document-discard" className="yb-tactile py-3 px-4 bg-[#DC2626] text-white font-bold flex items-center justify-center gap-2">
                  <Trash2 className="w-5 h-5" /> {t("del")}
                </button>
              </div>
            </div>
          )}

          {imgUrl && isImg && (
            <img src={imgUrl} alt={doc.title} className="w-full max-h-64 object-contain rounded-2xl border-3 border-[#1A1C19] bg-white" />
          )}

          {/* Tabs */}
          <div className="grid grid-cols-4 gap-2">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`doc-tab-${t.id}`}
                className={`yb-tactile py-3 font-bold flex flex-col sm:flex-row items-center justify-center gap-1 text-sm ${tab === t.id ? "bg-[#1A1C19] text-white" : "bg-white"}`}>
                <t.icon className="w-5 h-5" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "read" && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border-3 border-[#1A1C19] p-4">
                <p className="text-lg leading-relaxed">{doc.summary}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => speak(doc.spoken_text, 1.0, "read")} data-testid="read-aloud-play-button"
                  className="yb-tactile bg-[#FFD600] py-4 font-extrabold flex flex-col items-center gap-1">
                  {loadingKey === "read" ? <Loader2 className="w-7 h-7 animate-spin" /> : <Play className="w-7 h-7" />}
                  <span className="text-sm">{t("listen")}</span>
                </button>
                <button onClick={() => speak(doc.spoken_text, 0.75, "slow")} data-testid="read-aloud-speed-button"
                  className="yb-tactile bg-white py-4 font-extrabold flex flex-col items-center gap-1">
                  {loadingKey === "slow" ? <Loader2 className="w-7 h-7 animate-spin" /> : <Rewind className="w-7 h-7" />}
                  <span className="text-sm">{t("slow")}</span>
                </button>
                <button onClick={repeat} data-testid="read-aloud-repeat-button"
                  className="yb-tactile bg-white py-4 font-extrabold flex flex-col items-center gap-1">
                  <Repeat className="w-7 h-7" />
                  <span className="text-sm">{t("repeat")}</span>
                </button>
              </div>

              {doc.next_actions && doc.next_actions.length > 0 && (
                <div className="bg-[#DCFCE7] rounded-2xl border-3 border-[#15803D] p-4">
                  <h3 className="font-extrabold mb-2">{t("whatToDo")}</h3>
                  <ul className="space-y-1 list-disc pl-5 text-lg">
                    {doc.next_actions.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}

              <details className="bg-white rounded-2xl border-3 border-[#1A1C19] p-4">
                <summary className="font-bold cursor-pointer">{t("originalText")}</summary>
                <p className="mt-2 whitespace-pre-wrap text-[#50564F]">{doc.original_text}</p>
              </details>

              <div className="flex gap-2">
                <button onClick={() => setTab("share")} data-testid="document-share" className="yb-tactile bg-[#2563EB] text-white py-3 px-4 flex-1 font-bold flex items-center justify-center gap-2">
                  <Share2 className="w-5 h-5" /> {t("send")}
                </button>
                <button onClick={remove} data-testid="document-delete" className="yb-tactile bg-[#DC2626] text-white py-3 px-4 font-bold flex items-center justify-center gap-2">
                  <Trash2 className="w-5 h-5" /> {t("del")}
                </button>
              </div>
            </div>
          )}

          {tab === "ask" && (
            <div className="space-y-3">
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {messages.length === 0 && <p className="text-[#50564F]">{t("askEmpty")}</p>}
                {messages.map((m, i) => (
                  <div key={i} className={`p-3 rounded-2xl border-2 border-[#1A1C19] ${m.role === "user" ? "bg-[#FEFCE8] ml-8" : "bg-white mr-8"}`}>
                    <p className="text-lg">{m.text}</p>
                    {m.role === "assistant" && (
                      <button onClick={() => speak(m.text, 1.0, `msg-${i}`)} className="mt-2 text-[#2563EB] flex items-center gap-1 text-sm font-bold">
                        <Play className="w-4 h-4" /> {t("listen")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <input
                  data-testid="ask-text-input"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && doAskText()}
                  placeholder={t("askPlaceholder")}
                  className="flex-1 yb-tactile bg-white px-4 py-3 text-lg outline-none"
                />
                <button onClick={doAskText} disabled={asking} data-testid="ask-text-send" className="yb-tactile bg-[#15803D] text-white p-3">
                  {asking ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}
                </button>
              </div>
              <button
                onClick={recording ? stopRec : startRec}
                data-testid="ask-voice-button"
                className={`yb-tactile w-full py-4 font-extrabold flex items-center justify-center gap-2 ${recording ? "bg-[#DC2626] text-white yb-glow-red" : "bg-[#FFD600]"}`}
              >
                {recording ? <><Square className="w-6 h-6" /> {t("askVoiceStop")}</> : <><Mic className="w-6 h-6" /> {t("askVoice")}</>}
              </button>
            </div>
          )}

          {tab === "reply" && (
            <div className="space-y-3">
              {!reply ? (
                <button onClick={loadReply} disabled={replyLoading} data-testid="reply-generate" className="yb-tactile w-full py-5 bg-[#FFD600] font-extrabold text-lg flex items-center justify-center gap-2">
                  {replyLoading ? <Loader2 className="w-6 h-6 animate-spin" /> : <PenLine className="w-6 h-6" />} {t("replyGenerate")}
                </button>
              ) : (
                <>
                  <div className="bg-white rounded-2xl border-3 border-[#1A1C19] p-4">
                    <h3 className="font-extrabold mb-1">{t("replyLabel")}</h3>
                    <p className="whitespace-pre-wrap text-lg">{reply.reply_text}</p>
                  </div>
                  <div className="bg-[#FEFCE8] rounded-2xl border-3 border-[#1A1C19] p-4">
                    <p className="text-lg">{reply.explanation}</p>
                    <button onClick={() => speak(reply.explanation, 1.0, "reply-exp")} className="mt-2 text-[#2563EB] flex items-center gap-1 font-bold">
                      <Play className="w-5 h-5" /> {t("listen")}
                    </button>
                  </div>
                  <button onClick={() => { navigator.clipboard.writeText(reply.reply_text); toast.success(t("copied")); }} className="yb-tactile w-full py-3 bg-[#2563EB] text-white font-bold flex items-center justify-center gap-2">
                    <Share2 className="w-5 h-5" /> {t("replyCopy")}
                  </button>
                </>
              )}
            </div>
          )}

          {tab === "share" && (
            <div className="space-y-4" data-testid="share-tab">
              <div className="bg-[#DBEAFE] rounded-2xl border-3 border-[#2563EB] p-4 flex items-center gap-3">
                <Users className="w-8 h-8 text-[#2563EB]" />
                <p className="text-lg font-bold">{t("shareTitle")}</p>
              </div>

              <div className="bg-[#ECFDF5] rounded-2xl border-3 border-[#15803D] p-4 space-y-2">
                <button onClick={forwardAll} disabled={forwarding} data-testid="forward-all-btn"
                  className="yb-tactile w-full py-4 bg-[#25D366] text-white font-extrabold text-lg flex items-center justify-center gap-3">
                  {forwarding ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}
                  {forwarding ? t("preparingMedia") : t("forwardBtn")}
                </button>
                <p className="text-sm text-[#50564F] text-center">{t("forwardHint")}</p>
              </div>

              {helpers.length === 0 ? (
                <p className="text-[#50564F]">{t("noHelpers")}</p>
              ) : (
                <div className="space-y-2">
                  {helpers.map((h) => (
                    <div key={h.id} className="bg-white rounded-2xl border-3 border-[#1A1C19] p-3 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center border-2 border-[#1A1C19] shrink-0">
                        <Phone className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold truncate">{h.name}</p>
                        <p className="text-sm text-[#50564F]">{h.phone}</p>
                      </div>
                      <button onClick={() => shareToHelper(h)} disabled={sharing} data-testid={`share-helper-${h.id}`}
                        className="yb-tactile bg-[#25D366] text-white px-4 py-2 font-bold flex items-center gap-2">
                        {sharing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />} WhatsApp
                      </button>
                      <button onClick={() => removeHelper(h.id)} data-testid={`helper-delete-${h.id}`} className="yb-tactile bg-[#DC2626] text-white p-2">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-[#FEFCE8] rounded-2xl border-3 border-[#1A1C19] p-4 space-y-2">
                <h4 className="font-extrabold">{t("addHelper")}</h4>
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t("namePh")} data-testid="helper-name-input"
                  className="w-full yb-tactile bg-white px-4 py-3 outline-none" />
                <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder={t("phonePh")} data-testid="helper-phone-input"
                  className="w-full yb-tactile bg-white px-4 py-3 outline-none" />
                <button onClick={saveHelper} data-testid="helper-save" className="yb-tactile w-full py-3 bg-[#15803D] text-white font-bold flex items-center justify-center gap-2">
                  <Plus className="w-5 h-5" /> {t("add")}
                </button>
              </div>

              <button onClick={share} data-testid="native-share" className="yb-tactile w-full py-3 bg-white font-bold flex items-center justify-center gap-2">
                <Share2 className="w-5 h-5" /> {t("otherShare")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
